import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, User, Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.tsx';
import { API_URL } from '../config/api.ts';

interface UserPersonaModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
}

interface Persona {
    _id: string;
    title: string;
    content: string;
    isActive: boolean;
}

const UserPersonaModal: React.FC<UserPersonaModalProps> = ({
    isOpen,
    onClose,
    onBack
}) => {
    const { isDarkMode } = useTheme();
    const [personas, setPersonas] = useState<Persona[]>([]);
    const [loading, setLoading] = useState(false);

    // UI States
    const [showForm, setShowForm] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newContent, setNewContent] = useState('');

    // Global Enabled Switch (Persisted locally)
    const [userPersonaEnabled, setUserPersonaEnabled] = useState(() => {
        const saved = localStorage.getItem('userPersonaEnabled');
        return saved !== null ? saved === 'true' : false;
    });

    useEffect(() => {
        localStorage.setItem('userPersonaEnabled', String(userPersonaEnabled));
    }, [userPersonaEnabled]);

    // Fetch personas on mount
    useEffect(() => {
        if (isOpen) {
            fetchPersonas();
        }
    }, [isOpen]);

    const fetchPersonas = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/user/personas`, { credentials: 'include' });
            const data = await res.json();
            if (data.success) {
                setPersonas(data.personas);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!newTitle.trim() || !newContent.trim()) return;
        try {
            const res = await fetch(`${API_URL}/user/personas`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: newTitle, content: newContent }),
                credentials: 'include'
            });
            const data = await res.json();
            if (data.success) {
                setPersonas(data.personas);
                setShowForm(false);
                setNewTitle('');
                setNewContent('');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!window.confirm('Delete this user persona?')) return;
        try {
            const res = await fetch(`${API_URL}/user/personas/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await res.json();
            if (data.success) {
                setPersonas(data.personas);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleSelect = async (id: string) => {
        // Optimistic UI update
        const updated = personas.map(p => ({ ...p, isActive: p._id === id }));
        setPersonas(updated);

        try {
            await fetch(`${API_URL}/user/personas/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isActive: true }),
                credentials: 'include'
            });
            // Background update to ensure sync
            fetchPersonas();
        } catch (err) {
            console.error(err);
            fetchPersonas(); // Revert on error
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)'
        }} onClick={onClose}>
            <div style={{
                width: '600px',
                maxWidth: '90%',
                backgroundColor: isDarkMode ? '#1e293b' : 'white',
                borderRadius: '24px',
                padding: '24px',
                boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                color: isDarkMode ? 'white' : '#1e293b',
                maxHeight: '85vh'
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                            onClick={onBack}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                borderRadius: '50%'
                            }}
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            User Persona <User size={18} color="#a855f7" />
                        </h2>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b', cursor: 'pointer' }}><X size={20} /></button>
                </div>

                {/* Main Content */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px' }}>

                    {/* Master Switch */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                        borderRadius: '16px',
                        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : '#e2e8f0'}`
                    }}>
                        <div>
                            <div style={{ fontWeight: '600', fontSize: '15px' }}>Enable User Persona</div>
                            <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                Send your active persona details with every message
                            </div>
                        </div>
                        <button
                            onClick={() => setUserPersonaEnabled(!userPersonaEnabled)}
                            style={{
                                width: '52px',
                                height: '28px',
                                borderRadius: '14px',
                                border: 'none',
                                backgroundColor: userPersonaEnabled ? '#a855f7' : (isDarkMode ? '#374151' : '#cbd5e1'),
                                cursor: 'pointer',
                                position: 'relative',
                                transition: 'background-color 0.2s ease'
                            }}
                        >
                            <div style={{
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                position: 'absolute',
                                top: '3px',
                                left: userPersonaEnabled ? '27px' : '3px',
                                transition: 'left 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </button>
                    </div>

                    {/* Personas List */}
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>Loading personas...</div>
                    ) : showForm ? (
                        <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            borderRadius: '16px',
                            border: `1px solid ${isDarkMode ? 'rgba(168, 85, 247, 0.3)' : '#e2e8f0'}`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '16px' }}>New Persona</h3>
                            <input
                                placeholder="Example: Default, Adventurer, Sci-Fi..."
                                value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                                style={{
                                    padding: '10px',
                                    borderRadius: '8px',
                                    background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'white',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    color: isDarkMode ? 'white' : 'black'
                                }}
                            />
                            <textarea
                                placeholder="Describe yourself: Name, age, appearance, relationship with the character..."
                                value={newContent}
                                onChange={e => setNewContent(e.target.value)}
                                style={{
                                    padding: '10px',
                                    borderRadius: '8px',
                                    background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'white',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    color: isDarkMode ? 'white' : 'black',
                                    minHeight: '100px',
                                    resize: 'vertical'
                                }}
                            />
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button onClick={() => setShowForm(false)} style={{
                                    padding: '8px 16px', borderRadius: '8px', background: 'transparent', color: isDarkMode ? 'white' : 'black', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer'
                                }}>Cancel</button>
                                <button onClick={handleCreate} style={{
                                    padding: '8px 16px', borderRadius: '8px', background: '#a855f7', color: 'white', border: 'none', cursor: 'pointer'
                                }}>Save Persona</button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {personas.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '30px', color: isDarkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
                                    No personas created yet.
                                </div>
                            )}

                            {personas.map(persona => (
                                <div
                                    key={persona._id}
                                    onClick={() => handleSelect(persona._id)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '16px',
                                        borderRadius: '16px',
                                        backgroundColor: persona.isActive
                                            ? (isDarkMode ? 'rgba(168, 85, 247, 0.15)' : '#f3e8ff')
                                            : (isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc'),
                                        border: `1px solid ${persona.isActive ? '#a855f7' : (isDarkMode ? 'rgba(255,255,255,0.05)' : '#e2e8f0')}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{
                                        width: '20px', height: '20px', borderRadius: '50%',
                                        border: `2px solid ${persona.isActive ? '#a855f7' : (isDarkMode ? 'rgba(255,255,255,0.3)' : '#cbd5e1')}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        {persona.isActive && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#a855f7' }} />}
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: '600', fontSize: '15px' }}>{persona.title}</div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%'
                                        }}>
                                            {persona.content}
                                        </div>
                                    </div>

                                    <button
                                        onClick={(e) => handleDelete(persona._id, e)}
                                        style={{
                                            background: 'transparent', border: 'none',
                                            color: isDarkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8',
                                            cursor: 'pointer', padding: '8px'
                                        }}
                                        title="Delete persona"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}

                            <button
                                onClick={() => setShowForm(true)}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    padding: '12px',
                                    borderRadius: '16px',
                                    border: '1px dashed rgba(168, 85, 247, 0.4)',
                                    background: 'rgba(168, 85, 247, 0.05)',
                                    color: '#a855f7',
                                    cursor: 'pointer',
                                    marginTop: '10px',
                                    fontWeight: '500'
                                }}
                            >
                                <Plus size={18} /> Create New User Persona
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserPersonaModal;
